<template>
  <Modal name="contact-us" :compact="true" @before-open="handleOpen">
    <div class="contact-us-modal">
      <BravadoText tag="h3" variant="header3">Contact Bravado</BravadoText>
      <BravadoText tag="p" variant="subhead" class="contact-us-modal__hint">
        Please give us 24-48 hours to respond to your message.
      </BravadoText>
      <form class="contact-us-modal__form" @submit.prevent="handleSubmit">
        <BravadoTextInputV3
          v-model.trim="email"
          :is-valid="!errors.email"
          label="Email"
          placeholder="Enter your email"
          class="contact-us-modal__section"
        />
        <BravadoTextInputV3
          v-model.trim="message"
          multiline
          :is-valid="!errors.message"
          label="What can we do for you?"
          placeholder="Write a note here if you have any questions!"
          :hint="`${message.length}/256 char`"
          class="contact-us-modal__section"
        />
        <BravadoButtonV3
          type="submit"
          variant="accent"
          size="md"
          :disabled="!isLoading"
          class="contact-us-modal__section"
        >
          {{ loading ? 'Submitting...' : 'Send' }}
        </BravadoButtonV3>
      </form>
      <BravadoText tag="div" variant="subhead">
        This site is protected by reCAPTCHA and the Google
        <br />
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="nofollow noopener noreferrer"
          class="contact-us-modal__link"
        >
          Privacy Policy
        </a>
        <br />
        <a
          href="https://policies.google.com/terms"
          target="_blank"
          rel="nofollow noopener noreferrer"
          class="contact-us-modal__link"
        >
          Terms of Service
        </a>
      </BravadoText>
    </div>
  </Modal>
</template>

<script lang="ts">
import { defineComponent } from '@nuxtjs/composition-api';
import { loadJS } from '~/components/utils/browser/core';
import validate, { rules } from '~/components/utils/validate';
import BravadoTextInputV3 from '~/components/common/BravadoTextInputV3/BravadoTextInputV3.vue';
import BravadoButtonV3 from '~/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import Modal from '~/components/common/Modal/Modal.vue';

export default defineComponent({
  name: 'ContactUsModal',
  components: {
    Modal,
    BravadoTextInputV3,
    BravadoButtonV3,
  },
  data() {
    return {
      loaded: false,
      loading: false,
      email: '',
      message: '',
      errors: { email: undefined, message: undefined },
    };
  },
  computed: {
    isLoading() {
      return this.loaded && !this.loading;
    },
  },
  methods: {
    async handleOpen() {
      if (!this.loaded) {
        await loadJS(process.env.RECAPTCHA_URL);
        this.loaded = true;
      }
    },
    async handleSubmit() {
      if (!this.isLoading) return;

      const { errors, valid } = validate({
        email: rules.test(this.email, rules.email, 'Email address is invalid'),
        message: rules.test(this.message, rules.required, 'Message is required'),
      });

      this.errors = errors;

      if (!valid) return;

      try {
        this.loading = true;

        // @ts-ignore
        const token = await window.grecaptcha.execute(process.env.RECAPTCHA_TOKEN, { action: 'contact_us' });

        const { message } = await this.$api.$post('/profile/request_support.json', {
          contact_token: token,
          email: this.email,
          message: this.message,
        });

        this.$notice.success(message);
        this.$modal.hide();
        this.email = '';
        this.message = '';
      } catch (e) {
        this.$notice.error(e);
        this.$sentryUtils.error(e);
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>

<style scoped lang="scss">
.contact-us-modal {
  color: var(--theme-color-text);

  &__section {
    width: 100%;

    & + & {
      margin-top: 24px;
    }
  }

  &__form {
    position: relative;
    margin: 24px 0;
  }

  &__hint {
    margin-top: 12px;
  }

  &__link {
    color: var(--blue-60);
    text-decoration: underline;
  }
}
</style>
